import { useQuery, type QueryClient } from '@tanstack/react-query';
import { listActivity } from '@/services/DataService';
import type { ActivityEntityType } from '@/types/activity';

/** The prefix every timeline sits under, so one call can refresh them all. */
export const ACTIVITY_KEY = ['activity'] as const;

/** The cache key for one entity's timeline. */
export const activityKey = (entityType: ActivityEntityType, entityId: string) =>
  [...ACTIVITY_KEY, entityType, entityId];

/**
 * How much of a timeline is asked for at once.
 *
 * One page rather than paging: a timeline is short and is read whole. The total
 * comes back either way, which is what a "load older" control would need.
 */
export const ACTIVITY_PAGE_SIZE = 100;

/**
 * Reads one entity's timeline, newest first.
 *
 * Not painted optimistically: what a line says depends on how the server
 * resolved the people on it and where the event landed in the order, and a
 * hand-built row that then corrects itself reads worse than one that arrives
 * right.
 */
export const useActivity = (entityType: ActivityEntityType, entityId: string | null) =>
  useQuery({
    queryKey: activityKey(entityType, entityId ?? ''),
    queryFn: () => listActivity(entityType, entityId as string, ACTIVITY_PAGE_SIZE),
    enabled: Boolean(entityId),
  });

/**
 * The chat tools that add a line to a timeline.
 *
 * Named here rather than in each module's own invalidation, because a timeline
 * is not the thing the tool wrote to: attaching a page changes a task's
 * timeline without moving it on the board, and deleting a page changes the
 * timeline of every task that held it without touching a task at all. One
 * prefix covers every entity, so a tool added here refreshes whichever
 * timeline it reached.
 */
const TIMELINE_WRITING_TOOLS = [
  'task_create',
  'task_update',
  'task_attach_artifact',
  'task_detach_artifact',
  'delete_artifact',
];

/**
 * Refreshes the timelines after the agent has written, so the line for what it
 * just did appears on the panel someone is looking at.
 *
 * Gated on the tools that reach a timeline: invalidating after every tool round
 * would refetch an open timeline because an unrelated chart was drawn.
 */
export const invalidateActivityQueries = (
  queryClient: QueryClient,
  executedToolNames: string[],
) => {
  if (!executedToolNames.some((name) => TIMELINE_WRITING_TOOLS.includes(name))) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: ACTIVITY_KEY });
};
